import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, setActiveTenantProvider } from '../api/client';
import { useTenants } from '../api/queries';
import type { Tenant } from '../types/agenda';
import { Spinner } from '../components/Spinner';

interface TenantContextType {
  tenants: Tenant[];
  activeTenant: Tenant | null;
  switchTenant: (tenantId: string) => void;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const ACTIVE_TENANT_KEY = 'agendamgr_active_tenant_id';

export function TenantProvider({ children }: { children: ReactNode }) {
  // Fetch through react-query so a 401 (expired session) lands in the query
  // cache, where AuthGate's subscriber detects it and routes to the sign-in
  // screen. A raw fetch here would bypass that and dead-end the user.
  const { data: tenants = [], isLoading, isError, error } = useTenants();

  const [activeTenantId, setActiveTenantId] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_TENANT_KEY),
  );

  // The active tenant is derived from the loaded list and the chosen id,
  // defaulting to the first tenant when nothing is stored.
  const activeTenant =
    tenants.find((t) => t.id === activeTenantId) ?? tenants[0] ?? null;

  // Register the X-Tenant-ID provider before children's effects fire. All
  // layout effects run before any passive effect (and react-query fetches in
  // passive effects), so this guarantees tenant-scoped calls carry the header.
  useLayoutEffect(() => {
    setActiveTenantProvider(activeTenant ? () => activeTenant.id : null);
    return () => setActiveTenantProvider(null);
  }, [activeTenant]);

  // Apply tenant theme when the active tenant changes.
  useEffect(() => {
    if (!activeTenant) return;

    const root = document.documentElement;

    // Apply accent colors from tenant theme
    Object.entries(activeTenant.theme.accents).forEach(([key, color]) => {
      root.style.setProperty(`--accent-${key}`, color);
    });

    // Apply custom stylesheet if provided
    if (activeTenant.theme.stylesheet) {
      const styleId = 'tenant-theme-stylesheet';
      let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = activeTenant.theme.stylesheet;
    }
  }, [activeTenant]);

  const switchTenant = (tenantId: string) => {
    if (tenants.some((t) => t.id === tenantId)) {
      localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
      setActiveTenantId(tenantId);
      // Navigate to the agenda list with a full load: the agenda currently open
      // almost certainly doesn't exist in the other tenant (and the same ID would
      // mean something different there), and this refetches all tenant-scoped
      // data for the new tenant.
      window.location.assign('/');
    }
  };

  // Block children until a tenant is resolved, so tenant-scoped API calls never
  // fire before the X-Tenant-ID header provider is registered (which would 400).
  if (isLoading) {
    return <Spinner label="Loading…" />;
  }

  // A 401 means the session expired. AuthGate subscribes to the query cache and
  // will switch to the sign-in screen; show a spinner in the meantime rather
  // than a dead-end "No tenant available" message.
  if (isError && error instanceof ApiError && error.status === 401) {
    return <Spinner label="Signing in…" />;
  }

  if (!activeTenant) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <h1 className="login-title">No tenant available</h1>
          <p className="login-sub">
            {isError
              ? `Could not load your organizations: ${
                  error instanceof Error ? error.message : 'unknown error'
                }`
              : 'Your account is not a member of any organization. Contact an administrator.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <TenantContext.Provider value={{ tenants, activeTenant, switchTenant, isLoading }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
}
